/* eslint-disable @typescript-eslint/no-explicit-any */
// Single flag for the unified "Audit Log" surface. Trace steps and
// evidence share one panel, so they share one gate. (The former separate
// REACT_APP_EVIDENCE_UI_ENABLED flag was dropped — evidence is always
// shown alongside trace in the unified design.)
export const TRACE_UI_ENABLED =
	(window as any).__ENV__?.REACT_APP_TRACE_UI_ENABLED === "true" ||
	process.env.REACT_APP_TRACE_UI_ENABLED === "true";

// Clean citation architecture — typed `CitedTableArtifact` rendering path
// (plan §13.1, mirrors backend CITATION_ARTIFACTS_ENABLED). Off by default.
// Evaluated at call time so it can be toggled via window.__ENV__ without a
// rebuild and overridden in tests.
export const isCitationArtifactsEnabled = (): boolean => {
	const value =
		(window as any).__ENV__?.REACT_APP_CITATION_ARTIFACTS_ENABLED ??
		process.env.REACT_APP_CITATION_ARTIFACTS_ENABLED;
	return value === "1" || String(value).toLowerCase() === "true";
};

// Operator-only live citation harness report. Mirrors backend
// CITATION_HARNESS_LIVE_ENABLED. Off by default.
export const isCitationHarnessEnabled = (): boolean => {
	const value =
		(window as any).__ENV__?.REACT_APP_CITATION_HARNESS_ENABLED ??
		process.env.REACT_APP_CITATION_HARNESS_ENABLED;
	return value === "1" || String(value).toLowerCase() === "true";
};

// The Claude Code harness A/B has no frontend flag by design. A new chat sends
// no `chat_type` unless the URL pins one via `?type=`, and the backend resolves
// the default from CLAUDE_CODE_HARNESS_ENABLED on every request. Mirroring it
// here would mean two flags to flip and a frontend deploy to change the arm.
// R-1.5c evidence delivery UI — source-typed citation chips + the generic
// evidence-peek panel + lineage expansion over delivered citations (§17). New
// surface, gated off by default; evaluated at call time so window.__ENV__ can
// toggle it without a rebuild and tests can override it.
export const isEvidencePeekEnabled = (): boolean => {
	const value =
		(window as any).__ENV__?.REACT_APP_EVIDENCE_PEEK_ENABLED ??
		process.env.REACT_APP_EVIDENCE_PEEK_ENABLED;
	return value === "1" || String(value).toLowerCase() === "true";
};

// Audit-trail timeline — semantic one-line steps in the Audit Log panel plus
// narration routing (interim model prose moves out of the chat bubble and into
// the timeline lane). Off by default; with it off the panel keeps the verbose
// raw tool rows and every text block renders in the bubble as before.
// Evaluated at call time so window.__ENV__ can toggle it without a rebuild and
// tests can override it.
export const isAuditTimelineEnabled = (): boolean => {
	const value =
		(window as any).__ENV__?.REACT_APP_AUDIT_TIMELINE_ENABLED ??
		process.env.REACT_APP_AUDIT_TIMELINE_ENABLED;
	return value === "1" || String(value).toLowerCase() === "true";
};

// Cabinet v1 — the "/cabinet" catalog graph view and its sidebar entry. Gates
// the whole surface (nav item + route), so it MUST also be emitted into
// window.__ENV__ by docker-entrypoint.sh or it reads as OFF in every deployed
// env. Evaluated at call time so it can be toggled without a rebuild and
// overridden in tests.
export const isCabinetUiEnabled = (): boolean => {
	const value =
		(window as any).__ENV__?.REACT_APP_CABINET_UI_ENABLED ??
		process.env.REACT_APP_CABINET_UI_ENABLED;
	return value === "1" || String(value).toLowerCase() === "true";
};

// Citations open in their own browser tab: a cited PDF or source workbook
// opens on the file page (continuous scroll, the quote or cell highlighted, a
// way back to it) instead of the modal viewer. Off by default so a tenant can
// be switched either way while we learn which reads better. MUST also be
// emitted into window.__ENV__ by docker-entrypoint.sh.
export const isCitationTabEnabled = (): boolean => {
	const value =
		(window as any).__ENV__?.REACT_APP_CITATIONS_OPEN_IN_TAB ??
		process.env.REACT_APP_CITATIONS_OPEN_IN_TAB;
	return value === "1" || String(value).toLowerCase() === "true";
};

// Deal Room files upload straight from the browser to storage, in resumable
// chunks, instead of through the backend — the only way past Cloud Run's 32 MiB
// request limit. Off by default; anything the direct path can't take still goes
// through the old upload. MUST also be emitted by docker-entrypoint.sh.
export const isDirectUploadsEnabled = (): boolean => {
	const value =
		(window as any).__ENV__?.REACT_APP_DIRECT_UPLOADS_ENABLED ??
		process.env.REACT_APP_DIRECT_UPLOADS_ENABLED;
	return value === "1" || String(value).toLowerCase() === "true";
};

// Deal Room folder sharing: the owner can share a deal with everyone on the
// team, and the Deal Room shows who owns and who added what. Off by default;
// pairs with the backend's DEAL_ROOM_SHARING_ENABLED. MUST also be emitted by
// docker-entrypoint.sh.
export const isDealRoomSharingEnabled = (): boolean => {
	const value =
		(window as any).__ENV__?.REACT_APP_DEAL_ROOM_SHARING_ENABLED ??
		process.env.REACT_APP_DEAL_ROOM_SHARING_ENABLED;
	return value === "1" || String(value).toLowerCase() === "true";
};

// Loan Tape Extraction — the Templates card (and its `?compose=loan_tape` deep
// link) plus the chat @-mention template and its `?template=schema-extractor`
// deep link. All three open the same SchemaExtractorForm, so all three read
// this one flag; gating only the card would leave two working ways back in.
//
// UNLIKE every flag above, this one DEFAULTS ON. The others gate surfaces that
// were off everywhere until a deployment opted in. Loan Tape is live in every
// environment today, so a default-off flag would silently pull it from dev and
// demo as well — the opposite of the intent. Only an explicit "false"/"0" hides
// it, which is what a tenant that should not see it sets in clients.yaml.
export const isLoanTapeTemplateEnabled = (): boolean => {
	const value =
		(window as any).__ENV__?.REACT_APP_LOAN_TAPE_TEMPLATE_ENABLED ??
		process.env.REACT_APP_LOAN_TAPE_TEMPLATE_ENABLED;
	if (value === undefined || value === null || value === "") return true;
	const normalized = String(value).toLowerCase();
	return normalized !== "false" && normalized !== "0";
};

// The Analyst rail on /dashboard (DOP-441): a chat beside the KPI grid whose
// turns carry what the user is looking at. Off by default; with it off the
// drill-in's "Ask about this" keeps its old `/chat?prefill=` bridge. Evaluated
// at call time so window.__ENV__ can toggle it without a rebuild and tests can
// override it. Must be emitted by docker-entrypoint.sh or it reads as OFF.
export const isDashboardAnalystEnabled = (): boolean => {
	const value =
		(window as any).__ENV__?.REACT_APP_DASHBOARD_ANALYST_ENABLED ??
		process.env.REACT_APP_DASHBOARD_ANALYST_ENABLED;
	return value === "1" || String(value).toLowerCase() === "true";
};

// Portfolio monitoring as a template (DOP-447..453): the Templates card, the
// template page (recipe, period board, borrowers) and the run page (ledger,
// covenants, gaps, lock). Mirrors backend MONITORING_TEMPLATES_ENABLED. Off by
// default; evaluated at call time so window.__ENV__ can toggle it without a
// rebuild and tests can override it. Must be emitted by docker-entrypoint.sh
// or it reads as OFF in every deployed env.
export const isMonitoringEnabled = (): boolean => {
	const value =
		(window as any).__ENV__?.REACT_APP_MONITORING_ENABLED ??
		process.env.REACT_APP_MONITORING_ENABLED;
	return value === "1" || String(value).toLowerCase() === "true";
};

// Deal screening (DOP-494..499): the "Deal Screening" card in New workflow,
// the workflow page (deals, needs review, recipe briefs) and the memo run page.
// Mirrors backend DEAL_SCREENING_ENABLED. Off by default — built for one tenant
// (Turtleback) and opted into per deployment. Rides INSIDE the Workflows gate:
// with workflows off, this flag cannot bring the pages back on its own.
// Evaluated at call time so window.__ENV__ can toggle it without a rebuild and
// tests can override it. Must be emitted by docker-entrypoint.sh or it reads as
// OFF in every deployed env.
export const isDealScreeningEnabled = (): boolean => {
	const value =
		(window as any).__ENV__?.REACT_APP_DEAL_SCREENING_ENABLED ??
		process.env.REACT_APP_DEAL_SCREENING_ENABLED;
	return value === "1" || String(value).toLowerCase() === "true";
};

// The "Extract KPIs" workflow template (DOP-569): its card in New workflow and
// the `?template=extract_kpis` deep link. Mirrors backend KPI_EXTRACTION_ENABLED
// — without it a KPI workflow created here stays queued forever. Off by
// default: it is opted into per tenant, so a tenant that turns Workflows on
// for another template (Deal Screening) does not get it too. Rides INSIDE the
// Workflows gate. Evaluated at call time so window.__ENV__ can toggle it
// without a rebuild and tests can override it. Must be emitted by
// docker-entrypoint.sh or it reads as OFF in every deployed env.
export const isKpiExtractionEnabled = (): boolean => {
	const value =
		(window as any).__ENV__?.REACT_APP_KPI_EXTRACTION_ENABLED ??
		process.env.REACT_APP_KPI_EXTRACTION_ENABLED;
	return value === "1" || String(value).toLowerCase() === "true";
};

// The whole Workflows section — the index, every workflow page (KPI, loan
// tape, monitoring), the retired paths that redirect into it, the sidebar
// entry and the run-completion toast — as one gate. A tenant that has no use
// for extraction hides all of it with one line in clients.yaml.
//
// DEFAULTS ON, like isLoanTapeTemplateEnabled and for the same reason: the
// surface is live in every environment today, so an unset value must keep it
// and only an explicit "false"/"0" hides it. Evaluated at
// call time so window.__ENV__ can toggle it without a rebuild and tests can
// override it. Must be emitted by docker-entrypoint.sh.
export const isWorkflowsEnabled = (): boolean => {
	const value =
		(window as any).__ENV__?.REACT_APP_WORKFLOWS_ENABLED ??
		process.env.REACT_APP_WORKFLOWS_ENABLED;
	if (value === undefined || value === null || value === "") return true;
	const normalized = String(value).toLowerCase();
	return normalized !== "false" && normalized !== "0";
};

// Where a fresh sign-in lands. "/dashboard" is the historical landing that
// now redirects into Workflows, kept verbatim so sign-in behaves exactly as
// before for every tenant that has them; with them opted out that redirect
// would end in NotFound, so sign-in lands on the chat instead.
export const defaultLandingPath = (): string =>
	isWorkflowsEnabled() ? "/dashboard" : "/chat";
